import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { SAKTI_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import SaktiRkhApiService from '../services/saktiRkhApiService';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  createEditDraft,
  hasDraftChanges,
  toRkhDetailUpdatePayload,
  validateRkhDetailDraft,
} from '../utils/rkhDetailEdit';
import {
  SaktiFilterOptions,
  SaktiPaginationMeta,
  SaktiRkhDetailData,
  SaktiRkhFilters,
  SaktiRkhHarvesterListItem,
  SaktiRkhKpiData,
  SaktiRkhListItem,
  SaktiRkhTabKey,
} from '../types';

const EMPTY_OPTIONS: SaktiFilterOptions = {
  companies: [],
  estates: [],
  divisions: [],
};

const EMPTY_KPI: SaktiRkhKpiData = {
  total_rkh: 0,
  total_harvesters_fp_required: 0,
  total_harvest_area: 0,
};

const EMPTY_PAGINATION: SaktiPaginationMeta = {
  page: 1,
  limit: SAKTI_DEFAULTS.DEFAULT_LIMIT,
  total_items: 0,
  total_pages: 0,
};

const toRkhFilters = (filters: SaktiRkhFilters, page?: number): SaktiRkhFilters => ({
  ...filters,
  page: page ?? filters.page,
});

interface UseSaktiRkhDashboardResult {
  options: SaktiFilterOptions;
  loadingOptions: boolean;
  loadingKpi: boolean;
  loadingTable: boolean;
  activeTab: SaktiRkhTabKey;
  draftFilters: SaktiRkhFilters;
  appliedFilters: SaktiRkhFilters;
  kpi: SaktiRkhKpiData;
  rkhRows: SaktiRkhListItem[];
  harvesterRows: SaktiRkhHarvesterListItem[];
  rkhPagination: SaktiPaginationMeta;
  harvesterPagination: SaktiPaginationMeta;
  tableError: string | null;
  detailLoading: boolean;
  detailData: SaktiRkhDetailData | null;
  isDetailOpen: boolean;
  isEditMode: boolean;
  isSavingDetail: boolean;
  editDraft: SaktiRkhDetailData | null;
  isDetailDirty: boolean;
  isDetailValid: boolean;
  onDraftFilterChange: (nextFilters: SaktiRkhFilters) => void;
  applyFilters: () => Promise<void>;
  setActiveTab: (tab: SaktiRkhTabKey) => void;
  goToPage: (page: number) => Promise<void>;
  openDetail: (rkhId: string) => Promise<void>;
  closeDetail: () => void;
  startDetailEdit: () => void;
  cancelDetailEdit: () => void;
  updateDetailDraft: (nextDraft: SaktiRkhDetailData) => void;
  saveDetailEdit: () => Promise<void>;
  reloadAll: () => Promise<void>;
}

export const useSaktiRkhDashboard = (): UseSaktiRkhDashboardResult => {
  const toast = useToast();

  const initialFilters: SaktiRkhFilters = useMemo(
    () => ({
      date: getTodayInJakartaForApi(),
      company_id: SAKTI_DEFAULTS.DEFAULT_COMPANY_ID,
      estate_id: undefined,
      division_id: undefined,
      page: SAKTI_DEFAULTS.DEFAULT_PAGE,
      limit: SAKTI_DEFAULTS.DEFAULT_LIMIT,
    }),
    []
  );

  const [options, setOptions] = useState<SaktiFilterOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [loadingKpi, setLoadingKpi] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<SaktiRkhTabKey>('rkh');
  const [draftFilters, setDraftFilters] = useState<SaktiRkhFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<SaktiRkhFilters>(initialFilters);
  const [kpi, setKpi] = useState<SaktiRkhKpiData>(EMPTY_KPI);
  const [rkhRows, setRkhRows] = useState<SaktiRkhListItem[]>([]);
  const [harvesterRows, setHarvesterRows] = useState<SaktiRkhHarvesterListItem[]>([]);
  const [rkhPagination, setRkhPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [harvesterPagination, setHarvesterPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [tableError, setTableError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<SaktiRkhDetailData | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSavingDetail, setIsSavingDetail] = useState<boolean>(false);
  const [editDraft, setEditDraft] = useState<SaktiRkhDetailData | null>(null);
  const detailSessionRef = useRef<number>(0);

  const isDetailDirty = useMemo(
    () => (detailData && editDraft ? hasDraftChanges(detailData, editDraft) : false),
    [detailData, editDraft]
  );

  const isDetailValid = useMemo(() => {
    if (!editDraft) {
      return false;
    }

    return validateRkhDetailDraft(editDraft).isValid;
  }, [editDraft]);

  const resetDetailEditState = useCallback(() => {
    setIsEditMode(false);
    setIsSavingDetail(false);
    setEditDraft(null);
  }, []);

  const loadAllData = useCallback(
    async (filtersToUse: SaktiRkhFilters) => {
      setLoadingKpi(true);
      setLoadingTable(true);
      setTableError(null);

      try {
        const [kpiResponse, rkhResponse, harvestersResponse] = await Promise.all([
          SaktiRkhApiService.getKpi(filtersToUse),
          SaktiRkhApiService.getRkhList(filtersToUse),
          SaktiRkhApiService.getHarvestersList(filtersToUse),
        ]);

        if (kpiResponse.success) {
          setKpi(kpiResponse.data);
        }

        if (rkhResponse.success) {
          setRkhRows(rkhResponse.data);
          setRkhPagination(rkhResponse.pagination);
        }

        if (harvestersResponse.success) {
          setHarvesterRows(harvestersResponse.data);
          setHarvesterPagination(harvestersResponse.pagination);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Gagal memuat data RKH dashboard';
        setTableError(errorMessage);
        toast({
          title: 'Gagal memuat data RKH',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } finally {
        setLoadingKpi(false);
        setLoadingTable(false);
      }
    },
    [toast]
  );

  const loadFilterOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const fetchedOptions = await SaktiApiService.fetchFilterOptions();
      setOptions(fetchedOptions);

      const defaultCompany =
        fetchedOptions.companies.find((company) => company.id === SAKTI_DEFAULTS.DEFAULT_COMPANY_ID)
          ?.id ?? fetchedOptions.companies[0]?.id ?? SAKTI_DEFAULTS.DEFAULT_COMPANY_ID;

      const nextFilters: SaktiRkhFilters = {
        ...initialFilters,
        company_id: defaultCompany,
      };

      setDraftFilters(nextFilters);
      setAppliedFilters(nextFilters);
      await loadAllData(nextFilters);
    } finally {
      setLoadingOptions(false);
    }
  }, [initialFilters, loadAllData]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const onDraftFilterChange = useCallback((nextFilters: SaktiRkhFilters) => {
    setDraftFilters(nextFilters);
  }, []);

  const applyFilters = useCallback(async () => {
    const nextFilters = {
      ...draftFilters,
      date: draftFilters.date || getTodayInJakartaForApi(),
      page: SAKTI_DEFAULTS.DEFAULT_PAGE,
    };
    setAppliedFilters(nextFilters);
    await loadAllData(nextFilters);
  }, [draftFilters, loadAllData]);

  const goToPage = useCallback(
    async (page: number) => {
      const nextFilters = toRkhFilters(appliedFilters, page);
      setAppliedFilters(nextFilters);

      setLoadingTable(true);
      setTableError(null);
      try {
        if (activeTab === 'rkh') {
          const response = await SaktiRkhApiService.getRkhList(nextFilters);
          if (response.success) {
            setRkhRows(response.data);
            setRkhPagination(response.pagination);
          }
        } else {
          const response = await SaktiRkhApiService.getHarvestersList(nextFilters);
          if (response.success) {
            setHarvesterRows(response.data);
            setHarvesterPagination(response.pagination);
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Gagal memuat halaman data';
        setTableError(errorMessage);
      } finally {
        setLoadingTable(false);
      }
    },
    [activeTab, appliedFilters]
  );

  const openDetail = useCallback(
    async (rkhId: string) => {
      const sessionId = detailSessionRef.current + 1;
      detailSessionRef.current = sessionId;
      setIsDetailOpen(true);
      setDetailLoading(true);
      setDetailData(null);
      resetDetailEditState();

      try {
        const response = await SaktiRkhApiService.getRkhDetail(rkhId);
        if (detailSessionRef.current !== sessionId) {
          return;
        }

        if (response.success) {
          setDetailData(response.data);
        }
      } catch (error) {
        if (detailSessionRef.current !== sessionId) {
          return;
        }

        toast({
          title: 'Gagal memuat detail RKH',
          description:
            error instanceof Error
              ? error.message
              : 'Terjadi kesalahan saat memuat detail',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      } finally {
        if (detailSessionRef.current !== sessionId) {
          return;
        }

        setDetailLoading(false);
      }
    },
    [resetDetailEditState, toast]
  );

  const closeDetail = useCallback(() => {
    if (isSavingDetail) {
      return;
    }

    detailSessionRef.current += 1;
    setIsDetailOpen(false);
    setDetailData(null);
    resetDetailEditState();
  }, [isSavingDetail, resetDetailEditState]);

  const startDetailEdit = useCallback(() => {
    if (!detailData) {
      return;
    }

    setEditDraft(createEditDraft(detailData));
    setIsEditMode(true);
  }, [detailData]);

  const cancelDetailEdit = useCallback(() => {
    resetDetailEditState();
  }, [resetDetailEditState]);

  const updateDetailDraft = useCallback((nextDraft: SaktiRkhDetailData) => {
    setEditDraft(nextDraft);
  }, []);

  const saveDetailEdit = useCallback(async () => {
    if (isSavingDetail) {
      return;
    }

    if (!detailData || !editDraft) {
      toast({
        title: 'Draft tidak tersedia',
        description: 'Tidak ada perubahan yang dapat disimpan.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (!isDetailDirty) {
      toast({
        title: 'Tidak ada perubahan',
        description: 'Silakan ubah data sebelum menyimpan.',
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const validation = validateRkhDetailDraft(editDraft);
    if (!validation.isValid) {
      toast({
        title: 'Draft tidak valid',
        description: validation.errors[0] ?? 'Periksa kembali data RKH.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    setIsSavingDetail(true);
    const sessionId = detailSessionRef.current;

    try {
      const payload = toRkhDetailUpdatePayload(editDraft);
      const response = await SaktiRkhApiService.updateRkhDetail(detailData.rkh.id, payload);

      if (detailSessionRef.current !== sessionId) {
        return;
      }

      if (!response.success) {
        throw new Error('Gagal menyimpan detail RKH');
      }

      setDetailData(response.data);
      resetDetailEditState();
      await loadAllData(appliedFilters);
      toast({
        title: 'Detail RKH tersimpan',
        description: 'Perubahan berhasil disimpan.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      if (detailSessionRef.current !== sessionId) {
        return;
      }

      toast({
        title: 'Gagal menyimpan RKH',
        description:
          error instanceof Error ? error.message : 'Terjadi kesalahan saat menyimpan.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      if (detailSessionRef.current === sessionId) {
        setIsSavingDetail(false);
      }
    }
  }, [
    appliedFilters,
    detailData,
    editDraft,
    isDetailDirty,
    isSavingDetail,
    loadAllData,
    resetDetailEditState,
    toast,
  ]);

  const handleTabChange = useCallback(
    (tab: SaktiRkhTabKey) => {
      setActiveTab(tab);
      setAppliedFilters((prev) => ({
        ...prev,
        page: tab === 'rkh' ? rkhPagination.page : harvesterPagination.page,
      }));
    },
    [rkhPagination.page, harvesterPagination.page]
  );

  const reloadAll = useCallback(async () => {
    await loadAllData(appliedFilters);
  }, [appliedFilters, loadAllData]);

  return {
    options,
    loadingOptions,
    loadingKpi,
    loadingTable,
    activeTab,
    draftFilters,
    appliedFilters,
    kpi,
    rkhRows,
    harvesterRows,
    rkhPagination,
    harvesterPagination,
    tableError,
    detailLoading,
    detailData,
    isDetailOpen,
    isEditMode,
    isSavingDetail,
    editDraft,
    isDetailDirty,
    isDetailValid,
    onDraftFilterChange,
    applyFilters,
    setActiveTab: handleTabChange,
    goToPage,
    openDetail,
    closeDetail,
    startDetailEdit,
    cancelDetailEdit,
    updateDetailDraft,
    saveDetailEdit,
    reloadAll,
  };
};

export default useSaktiRkhDashboard;
